import { useState, useEffect } from 'react';
import './ReviewSubmit.css';

// PUBLIC review-submission page (no login) — reached from the emailed link
// /review/<token>. The contact rates + writes a short review; it lands in the
// agent's queue as 'pending' and only goes public once the agent approves it.

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="rv-stars-pick" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n} type="button"
          className={`rv-star ${n <= active ? 'on' : ''}`}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
          aria-pressed={n === value}
          onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n === value ? 0 : n)}
        >★</button>
      ))}
      {value > 0 && <button type="button" className="rv-star-clear" onClick={() => onChange(0)}>clear</button>}
    </div>
  );
}

function ReviewSubmit({ token }) {
  const [info, setInfo] = useState(null);
  const [state, setState] = useState('loading');   // loading | ok | notfound | done
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState('');
  const [status, setStatus] = useState('');   // '' | sending | error
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    fetch(`/api/reviews/submit/${encodeURIComponent(token)}`)
      .then(async r => {
        if (r.status === 404) { if (alive) setState('notfound'); return null; }
        if (!r.ok) { if (alive) setState('notfound'); return null; }
        return r.json();
      })
      .then(d => {
        if (!alive || !d) return;
        setInfo(d);
        setName(d.authorName || '');
        setState(d.alreadySubmitted ? 'done' : 'ok');
      })
      .catch(() => { if (alive) setState('notfound'); });
    return () => { alive = false; };
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) { setErr('Please add your name.'); return; }
    if (!body.trim()) { setErr('Please write a few words.'); return; }
    setStatus('sending'); setErr('');
    try {
      const r = await fetch(`/api/reviews/submit/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authorName: name, rating: rating || undefined, body }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not submit your review.');
      setState('done');
    } catch (e2) { setStatus('error'); setErr(e2.message || 'Could not submit your review.'); }
  }

  if (state === 'loading') return <div className="rv-state">Loading…</div>;
  if (state === 'notfound') return <div className="rv-state">This review link isn’t valid or has expired.</div>;

  const who = info?.agentName || 'your agent';

  if (state === 'done') {
    return (
      <div className="rv-page">
        <div className="rv-card rv-thanks">
          <div className="rv-check">✓</div>
          <h1>Thank you!</h1>
          <p>Your review of {who} has been submitted. They’ll review it before it appears on their profile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rv-page">
      <div className="rv-card">
        <div className="rv-head">
          {info?.agentPhoto
            ? <img className="rv-photo" src={info.agentPhoto} alt={who} />
            : <div className="rv-photo rv-photo-initial">{(who || '?').charAt(0).toUpperCase()}</div>}
          <div>
            <h1 className="rv-title">Review {who}</h1>
            <p className="rv-sub">Share a few words about your experience — it only takes a minute.</p>
          </div>
        </div>
        <form onSubmit={submit} className="rv-form">
          <label className="rv-label">Your rating</label>
          <StarPicker value={rating} onChange={setRating} />

          <label className="rv-label" htmlFor="rv-name">Your name</label>
          <input id="rv-name" className="rv-input" value={name} onChange={e => { setName(e.target.value); setErr(''); }} placeholder="Your name" />

          <label className="rv-label" htmlFor="rv-body">Your review</label>
          <textarea id="rv-body" className="rv-textarea" rows={5} value={body}
            onChange={e => { setBody(e.target.value); setErr(''); }}
            placeholder={`What was it like working with ${who}?`} />

          {err && <div className="rv-err">{err}</div>}
          <button className="rv-send" type="submit" disabled={status === 'sending'}>
            {status === 'sending' ? 'Submitting…' : 'Submit review'}
          </button>
          <p className="rv-fine">Your review is sent to {who} and appears publicly only after they approve it.</p>
        </form>
      </div>
    </div>
  );
}

export default ReviewSubmit;
